import { z } from "zod";
import { responseSchema } from "../http.js";
import { catalogValueSchema } from "./catalog.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Catálogo de productos
 *
 * ── Qué NO está aquí, y por qué ──────────────────────────
 * El modelo del sistema actual arrastra más de cincuenta campos, y buena
 * parte es maquinaria de compensación: `pv`, `cv`, `isQualifying`,
 * `qualifiesForRank`, `franchiseeDiscountPercent`, `bundleItems`,
 * `sizeIds`, `colorIds`, `extraCostIds`.
 *
 * Copiarlos ahora sería fijar el diseño del motor de comisiones antes de
 * haberlo pensado, y ese motor es el corazón del negocio. Este contrato
 * cubre el CATÁLOGO —qué es un producto, cuánto cuesta, cómo se ve— y
 * deja esos campos para su propia sección. Añadirlos después es extender
 * este esquema, no rehacerlo.
 * ═══════════════════════════════════════════════════════════
 */
// ── Dinero ───────────────────────────────────────────────
export const CURRENCIES = ["MXN", "USD"];
export const currencySchema = z.enum(CURRENCIES);
export const CURRENCY_SYMBOLS = {
    MXN: "$",
    USD: "$",
};
/**
 * Los precios viven en CENTAVOS enteros, nunca en decimales.
 *
 * 0.1 + 0.2 no es 0.3 en punto flotante, y un catálogo que se suma miles
 * de veces al día acumula ese error hasta que un total no cuadra y nadie
 * sabe dónde empezó. En centavos, la suma es exacta siempre.
 */
export const priceCentsSchema = z
    .number()
    .int("El precio debe ser un número entero de centavos")
    .min(0, "El precio no puede ser negativo")
    .max(100_000_000, "Ese precio es demasiado alto");
/**
 * Precio por moneda, con las llaves ABIERTAS.
 *
 * No es `{ MXN, USD }` fijo: en el sistema actual las monedas salen del
 * catálogo de países, así que abrir un mercado nuevo crea una moneda sola.
 * Fijarlas aquí obligaría a tocar el contrato, el modelo y el formulario
 * cada vez que el negocio entra a un país.
 *
 * Las llaves son códigos ISO 4217 de tres letras en mayúsculas: una llave
 * inventada se rechaza, así que "abierto" no significa "cualquier cosa".
 */
export const priceMapSchema = z.record(z.string().regex(/^[A-Z]{3}$/, "Usa un código de moneda de tres letras"), priceCentsSchema);
/** De centavos a lo que se enseña. Una sola función, para que no diverja. */
export function formatPrice(cents, currency, locale = "es-MX") {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
    }).format(cents / 100);
}
/** De lo que alguien escribe ("1,299.50") a centavos. `null` si no se entiende. */
export function parsePriceToCents(raw) {
    const cleaned = raw.replace(/[^0-9.,-]/g, "").replace(/,/g, "");
    if (!cleaned || !/^-?\d*\.?\d*$/.test(cleaned))
        return null;
    const value = Number(cleaned);
    if (!Number.isFinite(value) || value < 0)
        return null;
    return Math.round(value * 100);
}
// ── Puntos ───────────────────────────────────────────────
/**
 * Volume points (VP) de un producto.
 *
 * Es el primer campo de compensación que entra al contrato, y entra por
 * necesidad, no por ambición: el mínimo de un paquete se mide en VP —"tu
 * carrito tiene 0 VP, necesitas 100 VP"— así que sin esto un paquete no
 * puede saber cuánto vale.
 *
 * ENTERO a propósito. Los VP se suman miles de veces al mes para decidir
 * rangos y comisiones; un decimal ahí acumula error hasta que el corte
 * mensual no cuadra y nadie sabe dónde empezó. Es la misma razón por la
 * que el dinero va en centavos.
 *
 * Lo que sigue SIN estar aquí: el CV (volumen comisionable), que puede
 * diferir del VP, y todo el reparto por niveles. Eso es el motor de
 * compensación y merece su propio diseño.
 */
export const volumePointsSchema = z
    .number()
    .int("Los puntos deben ser un número entero")
    .min(0, "Los puntos no pueden ser negativos")
    .max(100000);
// ── Textos traducibles ───────────────────────────────────
/**
 * El contenido de cada producto en los dos idiomas.
 *
 * Va en el DOCUMENTO y no en los archivos de traducción de la interfaz:
 * el nombre de un producto lo escribe quien administra el catálogo, no
 * quien programa. Si viviera en un JSON del frontend, añadir un producto
 * exigiría un despliegue.
 */
export const productContentSchema = z.object({
    name: z.string().trim().min(2, "Escribe el nombre").max(120, "Ese nombre es muy largo"),
    tagline: z.string().trim().max(160, "Muy largo").optional(),
    description: z.string().trim().max(4000, "Muy largo").optional(),
    /**
     * La LEYENDA de la ficha: el aviso que acompaña al producto en la
     * tienda. "No es un medicamento", los alérgenos, la nota de la FDA.
     *
     * ── Por qué es traducible y `legend` no ─────────────────────────────
     * Ya existe `legend` suelto en el producto, en un solo idioma. Nació
     * como la letra pequeña de la etiqueta impresa —que es un dato del
     * envase, no de la tienda— y por eso vive fuera de `translations`.
     *
     * Este campo es lo que Anahí pidió en el paso 1: un texto que se
     * escribe JUNTO al nombre y la descripción, en español y en inglés,
     * porque un aviso legal en el idioma equivocado no avisa de nada. Los
     * dos conviven a propósito; el que la ficha pública debe preferir es
     * este, y `legend` queda como el respaldo del catálogo antiguo.
     *
     * El techo son 600 caracteres. Un aviso legal de más de un párrafo no
     * lo lee nadie, y el sitio de la letra pequeña larga es la ficha
     * técnica (`datasheet`), que es un documento aparte.
     */
    disclaimer: z.string().trim().max(600, "Muy largo").optional(),
    benefits: z.array(z.string().trim().min(1).max(200)).max(12).optional(),
});
export const LOCALES = ["es", "en"];
export const localeSchema = z.enum(LOCALES);
/**
 * Español obligatorio, inglés opcional.
 *
 * El negocio opera en México: exigir la traducción para poder guardar
 * dejaría productos sin publicar esperando a un traductor. Cuando falta
 * el inglés, la interfaz cae al español — que es información incompleta,
 * pero es mejor que un hueco en blanco.
 */
export const productTranslationsSchema = z.object({
    es: productContentSchema,
    en: productContentSchema.partial().optional(),
});
/** Devuelve el contenido del idioma pedido, cayendo al español. */
export function contentFor(translations, locale) {
    if (locale === "es")
        return translations.es;
    const en = translations.en;
    return {
        name: en?.name?.trim() || translations.es.name,
        tagline: en?.tagline?.trim() || translations.es.tagline,
        description: en?.description?.trim() || translations.es.description,
        disclaimer: en?.disclaimer?.trim() || translations.es.disclaimer,
        benefits: en?.benefits?.length ? en.benefits : translations.es.benefits,
    };
}
// ── Contenido editorial de la ficha ──────────────────────
//
// La ficha del sistema actual no es una tabla de datos: es una pieza
// editorial con pilares, ingredientes, cifras y un cierre. Ese contenido
// lo escribe quien conoce el producto, así que vive en el DOCUMENTO y no
// en el código — y por eso está en los dos idiomas, como el resto.
//
// TODO bloque es opcional: un producto sin editorial es válido y su ficha
// enseña lo que tenga. Media ficha es mejor que una plantilla con huecos
// que gritan "falta contenido".
/**
 * Iconos permitidos, por NOMBRE y no por componente.
 *
 * El contrato no puede importar de `lucide-react` —lo comparte el
 * servidor— así que viaja el nombre y el frontend lo resuelve. La lista
 * cerrada es a propósito: un icono inventado en la base no puede tumbar
 * la ficha, porque el esquema lo rechaza antes de llegar a la pantalla.
 */
export const EDITORIAL_ICONS = [
    "bolt", "dna", "shield", "leaf", "heart", "brain",
    "flask", "sparkles", "clock", "droplet", "sun", "activity",
    // Los que usa el editorial de PRODUCCIÓN (import del 28-ago-2026): sin
    // ellos, dos de cada tres tarjetas de ingredientes llegarían sin icono.
    "atom", "balance", "berry", "cell", "eye", "flower", "globe", "grape",
    "leaves", "link", "magnet", "marigold", "microscope", "palm",
    "pomegranate", "pulse", "refresh", "spark", "spiral", "target",
];
export const editorialIconSchema = z.enum(EDITORIAL_ICONS);
/** Claro u oscuro. Varios bloques se pintan en las dos versiones. */
export const EDITORIAL_THEMES = ["light", "dark"];
export const editorialThemeSchema = z.enum(EDITORIAL_THEMES);
export const EDITORIAL_CALLOUT_TYPES = ["science", "warning", "context", "quote"];
export const editorialCalloutTypeSchema = z.enum(EDITORIAL_CALLOUT_TYPES);
export const EDITORIAL_CLOSING_THEMES = ["accent", "dark", "light"];
export const editorialClosingThemeSchema = z.enum(EDITORIAL_CLOSING_THEMES);
/** Los cuatro datos de un vistazo, bajo la compra. */
export const editorialMetadataSchema = z.object({
    content: z.string().trim().max(60).optional(),
    dose: z.string().trim().max(60).optional(),
    duration: z.string().trim().max(60).optional(),
    cert: z.string().trim().max(60).optional(),
});
export const editorialPillarSchema = z.object({
    icon: editorialIconSchema.optional(),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(600).optional(),
});
export const editorialIngredientSchema = z.object({
    icon: editorialIconSchema.optional(),
    name: z.string().trim().min(1).max(80),
    /** El renglón pequeño en mayúsculas: "COENZIMA DE LA ENERGÍA CELULAR". */
    spec: z.string().trim().max(120).optional(),
    description: z.string().trim().max(600).optional(),
});
/** Una fila de la tabla nutrimental. */
export const editorialSpecRowSchema = z.object({
    name: z.string().trim().min(1).max(120),
    /** Cantidad, como TEXTO: hay dosis que son "150-300" o "c.s.p." */
    dose: z.string().trim().max(40).optional(),
    unit: z.string().trim().max(20).optional(),
    /** Para qué sirve ese ingrediente. */
    function: z.string().trim().max(200).optional(),
});
/** Un lado del bloque comparativo. */
export const editorialSideSchema = z.object({
    icon: editorialIconSchema.optional(),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(600).optional(),
});
export const editorialStatSchema = z.object({
    number: z.string().trim().min(1).max(24),
    label: z.string().trim().min(1).max(60),
    description: z.string().trim().max(120).optional(),
});
export const editorialPillarSectionSchema = z.object({
    title: z.string().trim().min(1).max(160),
    intro: z.string().trim().max(800).optional(),
    items: z.array(editorialPillarSchema).min(1).max(12),
});
export const editorialIngredientSectionSchema = z.object({
    title: z.string().trim().min(1).max(160),
    intro: z.string().trim().max(800).optional(),
    theme: editorialThemeSchema.optional(),
    items: z.array(editorialIngredientSchema).min(1).max(12),
});
export const editorialStatSectionSchema = z.object({
    title: z.string().trim().min(1).max(160),
    intro: z.string().trim().max(800).optional(),
    items: z.array(editorialStatSchema).min(1).max(12),
});
export const editorialSpecTableSchema = z.object({
    title: z.string().trim().min(1).max(160),
    /** "Información nutrimental", "Análisis por porción"… */
    type: z.string().trim().max(120).optional(),
    /** Texto pequeño al pie de la tabla. */
    footer: z.string().trim().max(600).optional(),
    items: z.array(editorialSpecRowSchema).min(1).max(40),
});
export const editorialSideBySideSchema = z.object({
    title: z.string().trim().min(1).max(160),
    theme: editorialThemeSchema.optional(),
    left: editorialSideSchema,
    right: editorialSideSchema,
});
export const editorialContentSchema = z.object({
    /** La etiqueta que va sobre la foto del héroe. */
    badgeLabel: z.string().trim().max(40).optional(),
    /** Precio tachado que se enseña en la ficha. */
    compareAt: priceMapSchema.optional(),
    metadata: editorialMetadataSchema.optional(),
    pillars: editorialPillarSectionSchema.optional(),
    ingredients: editorialIngredientSectionSchema.optional(),
    specTable: editorialSpecTableSchema.optional(),
    process: editorialPillarSectionSchema.optional(),
    stats: editorialStatSectionSchema.optional(),
    sideBySide: editorialSideBySideSchema.optional(),
    callout: z
        .object({
        type: editorialCalloutTypeSchema.optional(),
        icon: editorialIconSchema.optional(),
        title: z.string().trim().min(1).max(200),
        body: z.string().trim().max(1200).optional(),
    })
        .optional(),
    closing: z
        .object({
        quote: z.string().trim().min(1).max(600),
        theme: editorialClosingThemeSchema.optional(),
    })
        .optional(),
});
/** Español obligatorio si hay editorial; inglés opcional, como el resto. */
export const productEditorialSchema = z.object({
    es: editorialContentSchema,
    en: editorialContentSchema.optional(),
});
/**
 * El editorial del idioma pedido, cayendo al español BLOQUE a bloque.
 *
 * No campo a campo como en `contentFor`: un bloque a medio traducir
 * —título en inglés, ingredientes en español— se lee peor que el bloque
 * entero en un solo idioma. Aquí la unidad de traducción es el bloque.
 */
export function editorialFor(editorial, locale) {
    if (!editorial)
        return undefined;
    if (locale === "es")
        return editorial.es;
    const en = editorial.en;
    if (!en)
        return editorial.es;
    return {
        badgeLabel: en.badgeLabel ?? editorial.es.badgeLabel,
        compareAt: en.compareAt ?? editorial.es.compareAt,
        metadata: en.metadata ?? editorial.es.metadata,
        pillars: en.pillars ?? editorial.es.pillars,
        ingredients: en.ingredients ?? editorial.es.ingredients,
        specTable: en.specTable ?? editorial.es.specTable,
        process: en.process ?? editorial.es.process,
        stats: en.stats ?? editorial.es.stats,
        sideBySide: en.sideBySide ?? editorial.es.sideBySide,
        callout: en.callout ?? editorial.es.callout,
        closing: en.closing ?? editorial.es.closing,
    };
}
// ── Compensación ─────────────────────────────────────────
//
// Estos campos se CAPTURAN desde ya aunque el motor que los consume no
// exista (decisión de Anahí, 26-ago). Capturar datos es barato; volver a
// abrir cuarenta y cinco fichas cuando el motor llegue, no.
//
// Todos opcionales: un producto sin ellos sigue siendo válido, y el
// catálogo actual no se invalida por añadirlos.
/**
 * CV — volumen comisionable. Puede diferir del VP: hay productos que dan
 * puntos de rango pero no reparten comisión, y al revés. Por eso son dos
 * campos y no uno con un factor.
 */
export const commissionPointsSchema = volumePointsSchema;
/**
 * ── Aquí NO está `volumePoints`, y su ausencia es el arreglo ─────────
 *
 * Estuvo, y era una COPIA. El VP vivía a la vez suelto en el producto y
 * anidado aquí; el formulario tenía un solo control y escribía el mismo
 * número en los dos sitios "para que no pudieran discrepar" — que es la
 * confesión de que podían.
 *
 * Se buscó quién lo leía antes de quitarlo. El resultado: NADIE lo lee
 * como dato propio. El motor de comisiones no toca `compensation`
 * siquiera; `Purchase.volumePoints` y `PeriodStats.personalVolumePoints`
 * se alimentan del campo SUELTO. El único lector del anidado era un
 * respaldo en `package.service.ts` —`product.volumePoints ??
 * product.compensation?.volumePoints ?? 0`— que existía solo porque el
 * campo existía, y cuyo propio comentario ya llamaba "canónico" al
 * suelto.
 *
 * Así que el PV del producto es `product.volumePoints`, uno y solo uno, y
 * el CV es `commissionPoints`, aquí abajo. Dos campos porque son dos
 * datos distintos —60 VP y 45 CV en el mismo producto del seed—, no dos
 * copias del mismo.
 *
 * La migración de los documentos que traían el anidado la hace
 * `promoteLegacyVolumePoints` en el modelo, al leer.
 */
export const productCompensationSchema = z.object({
    /** CV comisionable. */
    commissionPoints: commissionPointsSchema.optional(),
    /** Puntos cuando el producto está en promoción. */
    promoVolumePoints: volumePointsSchema.optional(),
    promoCommissionPoints: commissionPointsSchema.optional(),
    /** Cuenta para calificar el periodo. */
    isQualifying: z.boolean().optional(),
    /** Suma al volumen PERSONAL de quien compra. */
    generatesPersonalPV: z.boolean().optional(),
    /** Suma al volumen de GRUPO de su línea ascendente. */
    generatesGroupPV: z.boolean().optional(),
    /** Cuenta para subir de rango. */
    qualifiesForRank: z.boolean().optional(),
    /** Para qué califica, en texto libre. */
    qualifiesForText: z.string().trim().max(300).optional(),
    /** Es un producto de recompra. */
    isRepurchase: z.boolean().optional(),
    /** Comprarlo mantiene la cuenta activa. */
    maintainsActive: z.boolean().optional(),
    membershipBenefitsText: z.string().trim().max(1000).optional(),
});
// ── Descuento ────────────────────────────────────────────
//
// Se escribe el descuento y el precio se DERIVA (decisión de Anahí,
// 26-ago). El sistema actual guarda además un precio promocional aparte,
// que puede contradecir al porcentaje; aquí no puede haber contradicción
// porque solo hay una fuente.
export const DISCOUNT_TYPES = ["percent", "fixed"];
export const discountTypeSchema = z.enum(DISCOUNT_TYPES);
export const productDiscountSchema = z.object({
    active: z.boolean(),
    type: discountTypeSchema,
    /** Porcentaje (0-100) o centavos, según `type`. */
    value: z.number().min(0),
});
// ── Los precios que se DERIVAN ───────────────────────────
//
// Tres precios por moneda (Anahí, 26-ago): público, socio y
// franquiciatario. Solo DOS se escriben.
//
// ── Por qué el de franquiciatario no se escribe ─────────────────────
// Ya existe `franchiseeDiscountPercent` (0–50). Un mapa
// `franchiseePrices` al lado sería una segunda fuente para el mismo
// número, y las dos fuentes se contradicen en cuanto alguien toca una:
// un 25 % junto a un precio que en realidad rebaja el 18 %, y la tienda
// prometiendo un ahorro que no existe. Es el mismo error que
// `package.ts` documenta y evita —"el descuento se escribe, el precio se
// deriva"— y el mismo que `promoPrices` cometía aquí.
//
// ── Por qué el de SOCIO sí se escribe ───────────────────────────────
// Porque NO es un descuento: es un importe negociado por producto y por
// moneda. Los números del catálogo sembrado lo demuestran solos —EVA
// Plus va de 1,470.00 a 1,250.00 en pesos (14.97 %) y de 74.00 a 63.00
// en dólares (14.86 %)— y no hay un porcentaje que produzca los dos.
// Derivarlo obligaría a cambiar precios que alguien decidió a mano, o a
// guardar un porcentaje con decimales por moneda, que es un mapa
// escrito con pasos extra. Se queda escrito, y se dice por qué.
/**
 * Aplica un porcentaje a un importe en centavos.
 *
 * Redondea al centavo, y hacia ABAJO: si el redondeo tiene que
 * equivocarse, que sea a favor de quien compra. Un centavo de más
 * multiplicado por miles de cobros es una reclamación; uno de menos, no.
 *
 * Vive aquí y no en `package.ts` —donde nació— porque ahora la usan los
 * dos y `package.ts` ya importa de este archivo: al revés sería un ciclo.
 * Allí se conserva el nombre `applyDiscount`, que es el que usan su
 * servicio y sus pruebas, delegando en esta.
 */
export function applyPercentDiscount(cents, percent) {
    return Math.floor(cents * (1 - percent / 100));
}
/**
 * El mismo porcentaje sobre TODAS las monedas del mapa.
 *
 * Recorre las llaves REALES y no nombra ninguna. Enumerar "MXN y USD" a
 * mano sobre un mapa abierto es lo que dejaba un producto en euros con
 * el precio tachado y sin precio debajo: media función cerrada bajo una
 * puerta abierta.
 */
export function percentPriceMap(map, percent) {
    const out = {};
    for (const [currency, cents] of Object.entries(map)) {
        out[currency] = applyPercentDiscount(cents, percent);
    }
    return out;
}
/**
 * El precio de FRANQUICIATARIO de cada moneda.
 *
 * La base es el precio de SOCIO cuando lo hay, y el público cuando no:
 * `franchiseeDiscountPercent` es, en palabras del contrato, el
 * porcentaje "extra para franquiciatarios activos", y una franquiciataria
 * es socia antes que franquiciataria. Aplicarlo sobre el público le
 * cobraría de más a quien ya tenía un precio mejor por su membresía.
 *
 * Devuelve el mapa VACÍO cuando no hay porcentaje: sin descuento no hay
 * un tercer precio, y un mapa igual al de socio invitaría a creer que sí.
 */
export function franchiseePrices(prices, memberPrices, percent) {
    if (!percent)
        return {};
    const out = {};
    for (const currency of currenciesOfMaps(prices, memberPrices)) {
        const base = memberPrices?.[currency] ?? prices[currency];
        if (base === undefined)
            continue;
        out[currency] = applyPercentDiscount(base, percent);
    }
    return out;
}
/**
 * El precio EN PROMOCIÓN de cada moneda, a partir del descuento escrito.
 *
 * `percent` rebaja proporcionalmente; `fixed` resta el mismo importe en
 * todas las monedas y por eso NO se usa para derivar: restar 150 pesos y
 * 150 dólares es la misma cifra y dos rebajas muy distintas. Con un
 * descuento fijo el mapa sale vacío a propósito, y el paso lo dice.
 *
 * Nunca baja de cero: un descuento mayor que el precio deja el producto
 * en 0, no en negativo.
 */
export function promoPricesFrom(prices, discount) {
    if (!discount?.active || discount.value <= 0)
        return {};
    if (discount.type === "fixed")
        return {};
    if (discount.value > 100)
        return {};
    return percentPriceMap(prices, discount.value);
}
/** La unión ordenada de las llaves de varios mapas de precio. */
export function currenciesOfMaps(...maps) {
    const codes = new Set();
    for (const map of maps) {
        for (const code of Object.keys(map ?? {}))
            codes.add(code);
    }
    return [...codes].sort();
}
// ── Vigencia, envío y ficha técnica ──────────────────────
export const productValiditySchema = z.object({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    /** Días que dura lo que se compra (membresías, programas). */
    durationDays: z.number().int().min(0).max(3650).optional(),
});
/**
 * La ventana de venta al revés: el fin antes del inicio.
 *
 * Devuelve el mensaje o `null`. NO es un `.refine` sobre el esquema, y
 * eso es deliberado: `productFieldsSchema` se somete después a
 * `.partial()` y a `.omit()` para armar el esquema de edición y el
 * cuerpo del PATCH, y una comprobación colgada del objeto convierte el
 * esquema en otro tipo que ya no admite ninguna de las dos operaciones.
 *
 * Como función suelta la usan los TRES sitios que la necesitan —el
 * formulario, el alta y la edición— sin que ninguno pueda olvidarla y
 * sin partir el esquema en dos versiones.
 *
 * Una promoción que termina antes de empezar no se vende nunca: no es un
 * detalle estético, es una promoción que quien la creó cree publicada.
 */
export function validityWindowError(validity) {
    if (!validity?.from || !validity.to)
        return null;
    return validity.to < validity.from
        ? "El fin de la vigencia no puede ir antes del inicio"
        : null;
}
/**
 * Dimensiones para el envío.
 *
 * El documento de dirección pide estandarizarlas en el producto para que
 * pasen solas a la paquetería y el usuario final no las teclee. Un
 * paquete suma las de sus partes.
 */
export const productShippingSchema = z.object({
    requiresShipping: z.boolean().optional(),
    weightGrams: z.number().int().min(0).optional(),
    lengthCm: z.number().min(0).optional(),
    widthCm: z.number().min(0).optional(),
    heightCm: z.number().min(0).optional(),
});
/**
 * Una dirección de archivo del catálogo: foto, ficha técnica, lo que
 * venga. Dos formas, las dos válidas.
 *
 *  · Una dirección COMPLETA —`https://…`— que apunta al servidor de
 *    otro: es lo que se pega cuando el archivo ya vive publicado.
 *  · Una ruta DEL PROPIO SITIO —`/fichas/sexto-elemento.pdf`— para un
 *    archivo que viaja dentro del repositorio.
 *
 * Una ruta relativa SIN la barra inicial (`fichas/algo.pdf`) sigue
 * rechazada a propósito: se resolvería contra la página que la enseña,
 * así que el mismo archivo se buscaría en un sitio distinto según se
 * mire desde `/tienda` o desde `/tienda/detox-elemental`.
 *
 * ── Por qué está AQUÍ arriba y es UNA sola ──────────────────────────
 * Esta regla existía —`imageRefSchema`, más abajo— y la ficha técnica no
 * la usaba: `productDatasheetSchema.url` era `z.url()` a secas. La
 * consecuencia no fue un mensaje feo, fue que la MISMA dirección se
 * aceptaba para una foto y se rechazaba para un PDF, y que los cuatro
 * productos sembrados quedaban imposibles de guardar desde el panel en
 * cuanto alguien les pusiera una ficha del propio sitio.
 *
 * Y muerde en cuatro sitios a la vez, no en uno: este esquema vigila la
 * ENTRADA (`productInputSchema`, `productUpdateSchema`) y también la
 * SALIDA (`productSchema`, que se vuelve a analizar en el navegador). Un
 * valor que entrara por el seed o por la consola de Mongo no daría un
 * 400 al guardar: tumbaría el GET del producto con un 500 y la ficha
 * dejaría de poder leerse. Por eso la regla es una y se comparte.
 */
export const urlOrSitePathSchema = z
    .string()
    .refine((value) => z.url().safeParse(value).success || /^\/[^\s]+$/.test(value), "Esa dirección no es válida: usa https://… o una ruta del sitio (/fichas/…)");
export const productDatasheetSchema = z.object({
    url: urlOrSitePathSchema.optional().or(z.literal("")),
    name: z.string().trim().max(120).optional(),
});
// ── Datos de etiqueta (la "ficha" que trae el bote) ──────
//
// Lo que producción guardaba suelto en cada producto —SKU, formato,
// porción, uso sugerido, fabricante, certificaciones, ingredientes y
// contraindicaciones— y que el contrato nuevo no tenía dónde poner
// (Anahí, 28-ago: "si no hay un campo con esa información, créalo").
// Viven agrupados y NO se traducen: son datos del envase, no de la
// tienda. La ficha pública los enseña en "Datos del producto".
export const productFactsSchema = z.object({
    sku: z.string().trim().max(40).optional(),
    format: z.string().trim().max(60).optional(),
    servingSize: z.string().trim().max(80).optional(),
    servingsPerContainer: z.number().int().min(0).max(10_000).optional(),
    suggestedUse: z.string().trim().max(300).optional(),
    manufacturedIn: z.string().trim().max(80).optional(),
    manufacturedFor: z.string().trim().max(200).optional(),
    certifications: z.array(z.string().trim().min(1).max(60)).max(12).optional(),
    ingredients: z.array(z.string().trim().min(1).max(800)).max(40).optional(),
    contraindications: z.array(z.string().trim().min(1).max(400)).max(20).optional(),
});
// ── Clasificación ────────────────────────────────────────
/**
 * El TIPO de ítem. Ya NO es un `z.enum` cerrado.
 *
 * ── Lo que se rompía al abrirlo, y lo que no ────────────────────────
 * Antes de tocarlo se buscó `type` en el repositorio entero. El
 * resultado importa: NO hay una sola rama de negocio que dependa de un
 * valor concreto. Ni el carrito, ni el cobro, ni el cálculo de
 * comisiones, ni la tienda miran `product.type`; el módulo de paquetes
 * tenía su propio `packageKind` (retirado el 2-oct-2026). `type` era, hoy, una ETIQUETA.
 *
 * Lo que sí dependía del enum eran tres redes, todas de compilación:
 *  1. `Record<ProductType, string>` en el panel, que obligaba a poner
 *     etiqueta a cada tipo nuevo o dejaba de compilar.
 *  2. `enumField(PRODUCT_TYPES, …)` en el modelo de Mongo.
 *  3. Una prueba que comprobaba que `"pocion-magica"` se rechazaba.
 * Las tres se sustituyen, no se pierden: la lista vive ahora en la base
 * y el modelo comprueba la FORMA del valor.
 *
 * ── Y lo que sí ata comportamiento a valores concretos ──────────────
 * El paso de clasificación cambia según el tipo (promoción pide fechas,
 * paquete pide componentes). Eso se resuelve en `catalog.ts` con
 * `behavior`: la lista de nombres queda abierta y cada nombre declara
 * cuál de los TRES comportamientos tiene. La lógica ramifica sobre el
 * comportamiento —lista cerrada, bajo control de quien programa— y
 * nunca sobre el nombre.
 *
 * Los valores del enum anterior siguen siendo opciones de sistema
 * (`SYSTEM_PRODUCT_TYPES`), así que ningún producto sembrado quedó
 * inválido.
 */
export const productTypeSchema = catalogValueSchema;
/**
 * La CATEGORÍA, con la misma forma que el tipo.
 *
 * Era `z.string().trim().max(60)` a secas, o sea texto libre: "Detox",
 * "detox" y "DETOX" entraban como tres categorías distintas y la tienda
 * partía el catálogo en tres montones. Ahora comparte la forma del
 * catálogo —minúsculas, números y guiones— que es exactamente la que ya
 * tenían las categorías sembradas (`detox`, `longevidad`, `femenino`),
 * así que ningún documento existente cambió.
 *
 * Lo que se ESCRIBE en el panel sigue siendo "Detox" con mayúscula: la
 * etiqueta vive en el catálogo y el producto guarda el valor.
 */
export const productCategorySchema = catalogValueSchema;
/*
 * `packageKind` (inicial / membresía / recompra) se retiró el 2-oct-2026:
 * era un campo fantasma —sin control en el panel desde el 26-ago y sin
 * una sola regla que lo leyera— que repetía lo que ya dice `type`. Si
 * algún día hace falta distinguir clases de paquete, va en el `behavior`
 * del catálogo, no en un segundo campo que se desincroniza del primero.
 */
// ── Composición de un paquete ────────────────────────────
/**
 * Una línea de la composición: qué producto y cuántas unidades.
 *
 * ── Es la MISMA forma que `packageItemSchema` ───────────────────────
 * Y no una copia: `package.ts` importa esta definición y la reexporta
 * con su nombre de siempre. Dos definiciones gemelas del mismo par
 * `{productId, quantity}` divergen en el primer cambio de techo, y
 * entonces un paquete admite 99 unidades por un camino y 60 por el otro.
 *
 * Vive AQUÍ y no allá porque `package.ts` ya importa de este archivo:
 * al revés sería un ciclo.
 */
export const catalogItemSchema = z.object({
    productId: z.string().min(1),
    quantity: z.number().int().min(1).max(99),
});
// ── Beneficios ───────────────────────────────────────────
/**
 * Los beneficios ELEGIDOS de un catálogo, en el orden en que se leen.
 *
 * ── Qué había antes, y qué se rompía ────────────────────────────────
 * `translations.<idioma>.benefits`: texto libre, uno por idioma y sin
 * relación entre los dos. "Apoya la función hepática" se volvía a
 * teclear en cada ficha, con sus erratas, y el inglés era un array
 * paralelo que se podía quedar con distinto número de renglones. Nada
 * decía que dos productos prometían lo mismo.
 *
 * ── Qué hay ahora ───────────────────────────────────────────────────
 * El beneficio es una ENTIDAD del catálogo (`kind: "benefit"`) con su
 * texto en los dos idiomas, y el producto guarda a cuáles se acoge. Se
 * escribe una vez y se corrige una vez.
 *
 * Es una LISTA ORDENADA y no un conjunto: el primer beneficio se lee
 * siempre y el sexto casi nunca, así que el orden es información del
 * producto y no del catálogo.
 *
 * ── Y `translations.<idioma>.benefits` sigue existiendo ─────────────
 * No es un descuido ni una duplicidad: es el texto YA RESUELTO, y lo
 * escribe el SERVIDOR a partir de estas referencias. La tienda lo lee
 * como siempre —sin una consulta más, sin un endpoint público nuevo, sin
 * tocar `contentFor`— y los productos que todavía no han pasado por el
 * catálogo conservan lo que tenían escrito. Quien manda es este campo;
 * el otro es su resultado, igual que `package.prices` es el resultado de
 * `package.discountPercent`.
 */
export const productBenefitsSchema = z.array(catalogValueSchema).max(12);
// ── Variantes y costos adicionales ───────────────────────
/**
 * Una variante o costo extra del producto: qué es y cuánto suma.
 *
 * Es lo que el sistema actual llamaba "Opciones" —colores, tallas,
 * materiales, costos extra— repartido en tres listas de identificadores
 * (`sizeIds`, `colorIds`, `extraCostIds`) que el contrato dejó fuera a
 * propósito. Vuelve como UNA lista, porque las tres eran lo mismo: una
 * opción con nombre y, a veces, un sobreprecio.
 *
 * ── El NOMBRE y la DESCRIPCIÓN salen del catálogo ───────────────────
 * `value` apunta a una opción de `kind: "extra_cost"`, que lleva las dos
 * cosas en los dos idiomas. "Talla XL" y lo que significa se escriben
 * una vez, no una vez por ficha y por idioma.
 *
 * ── El MONTO es del producto, y va POR MONEDA ───────────────────────
 * Esto es lo que no puede vivir en el catálogo: el envío express cuesta
 * lo que cuesta ESTE envío. Y es un mapa de precios como todos los
 * demás, no un número suelto, porque 200 no significa lo mismo en pesos
 * que en dólares: un solo número obligaría a elegir en qué moneda está
 * escrito, y la otra saldría cien veces mal en la tienda equivocada.
 *
 * Centavos enteros, como todo el dinero de este archivo.
 *
 * El mapa puede venir VACÍO: hay variantes que no cuestan nada —un color
 * distinto al mismo precio— y obligar a poner 0 en cada moneda para
 * decir "no cuesta" es trabajo que no dice nada.
 */
export const productExtraCostSchema = z.object({
    /** La opción del catálogo `extra_cost` a la que se acoge. */
    value: catalogValueSchema,
    /** Lo que suma, por moneda y en centavos. Vacío = no cuesta nada. */
    amounts: priceMapSchema,
});
/**
 * El techo son 20 líneas. Veinte variantes de un mismo producto ya no
 * son variantes, son productos; el número existe para que un error de
 * tecleo no escriba un documento de miles de líneas.
 */
export const productExtraCostsSchema = z.array(productExtraCostSchema).max(20);
// ── El producto ──────────────────────────────────────────
export const PRODUCT_STATUSES = ["draft", "published", "archived"];
export const productStatusSchema = z.enum(PRODUCT_STATUSES);
/**
 * `slug`: la parte legible de la URL del producto.
 * Se elige al crear y NO se cambia: un slug que cambia rompe cada enlace
 * ya compartido y cada resultado de búsqueda indexado.
 */
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, "Muy corto")
    .max(80, "Muy largo")
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Solo minúsculas, números y guiones");
export function slugify(raw) {
    return raw
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}
export const productSchema = z.object({
    id: z.string(),
    slug: z.string(),
    translations: productTranslationsSchema,
    category: productCategorySchema.optional(),
    prices: priceMapSchema,
    /** Precio tachado, para enseñar una rebaja. Debe ser mayor que el precio. */
    compareAtPrices: priceMapSchema.optional(),
    imageUrl: z.string().optional(),
    imageUrls: z.array(z.string()).max(8).optional(),
    presentation: z.string().trim().max(120).optional(),
    stock: z.number().int().min(0).optional(),
    /** Posología, tal como va en la etiqueta. */
    dosage: z.string().trim().max(200).optional(),
    /** Aviso legal de la ficha pública: no es medicamento, alérgenos, FDA… */
    legend: z.string().trim().max(2000).optional(),
    type: productTypeSchema.optional(),
    /**
     * Los productos que COMPONEN este, cuando su tipo se comporta como
     * paquete. Vacío o ausente en todo lo demás.
     *
     * Decisión de Anahí (26-ago): la composición se arma en el formulario
     * de PRODUCTO. `/admin/packages` sigue viva y sin tocar mientras se
     * planifica su retirada; por eso este campo y `package.items` conviven
     * hoy, con la misma forma exacta para que migrar sea copiar y no
     * traducir.
     *
     * El techo son 60 líneas. Un paquete de sesenta productos distintos ya
     * no es un paquete, es un catálogo, y el número existe para que un
     * error de tecleo no escriba un documento de miles de líneas.
     */
    items: z.array(catalogItemSchema).max(60).optional(),
    /** Los beneficios del catálogo a los que se acoge, en orden. */
    benefits: productBenefitsSchema.optional(),
    /** Variantes y costos adicionales: qué son y cuánto suman por moneda. */
    extraCosts: productExtraCostsSchema.optional(),
    /** Precio para socios. Mismo mapa que `prices`. Se ESCRIBE: ver `franchiseePrices`. */
    memberPrices: priceMapSchema.optional(),
    /**
     * Precio en promoción. Se DERIVA de `discount` (ver `promoPricesFrom`)
     * y lo escribe el servidor: dos fuentes para la misma rebaja se
     * contradicen en cuanto alguien toca una.
     */
    promoPrices: priceMapSchema.optional(),
    discount: productDiscountSchema.optional(),
    compensation: productCompensationSchema.optional(),
    validity: productValiditySchema.optional(),
    shipping: productShippingSchema.optional(),
    datasheet: productDatasheetSchema.optional(),
    facts: productFactsSchema.optional(),
    /** Mayorista: paquete de negocio digital. No sale en paquetes personales. */
    isWholesale: z.boolean().optional(),
    /** % extra para franquiciatarios activos. */
    franchiseeDiscountPercent: z.number().min(0).max(50).optional(),
    /** Publicado pero temporalmente sin vender. */
    isAvailable: z.boolean().optional(),
    volumePoints: volumePointsSchema.optional(),
    editorial: productEditorialSchema.optional(),
    status: productStatusSchema,
    sortOrder: z.number().int().default(0),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
// ── Crear y editar ───────────────────────────────────────
/**
 * La dirección de la FOTO de un producto: la MISMA regla que la de la
 * ficha técnica.
 *
 * Es un alias con nombre propio, no una copia. `urlOrSitePathSchema`
 * (más arriba) es la definición; aquí solo se le pone el nombre con el
 * que se lee en los campos de imagen. Dos expresiones gemelas divergen
 * en el primer retoque, y entonces la misma dirección se acepta para una
 * foto y se rechaza para un PDF — que es exactamente el fallo que esta
 * unificación viene a cerrar.
 */
const imageRefSchema = urlOrSitePathSchema;
/**
 * Los campos editables, SIN valores por omisión.
 *
 * Existe separado por una trampa que costó dos parches: en Zod, `.partial()`
 * NO quita los `.default()`. Un esquema con `status: z.default("draft")`
 * hecho parcial sigue devolviendo `status: "draft"` al analizar un objeto
 * vacío — así que un PATCH para corregir una falta de ortografía
 * DESPUBLICABA el producto, y otro para archivarlo le borraba su orden en
 * el catálogo. Sin ruido, sin error: el campo simplemente aparecía.
 *
 * Los valores por omisión pertenecen a la CREACIÓN, donde "no me lo
 * dijiste" significa de verdad "usa el valor de partida". En una edición
 * parcial, "no me lo dijiste" significa "no lo toques".
 */
const productFieldsSchema = z.object({
    translations: productTranslationsSchema,
    category: productCategorySchema.optional(),
    /*
     * "Al menos UNA moneda", no "MXN o USD".
     *
     * La regla anterior nombraba las dos monedas de hoy y anulaba en la
     * práctica el mapa abierto: un producto con precio solo en euros se
     * rechazaba. Abrir las llaves y dejar la comprobación cerrada es tener
     * la puerta abierta con el cerrojo puesto.
     */
    prices: priceMapSchema.refine((p) => Object.keys(p).length > 0, "Pon al menos un precio"),
    compareAtPrices: priceMapSchema.optional(),
    imageUrl: imageRefSchema.optional().or(z.literal("")),
    imageUrls: z.array(imageRefSchema).max(8).optional(),
    presentation: z.string().trim().max(120).optional(),
    stock: z.number().int().min(0).optional(),
    /** Posología, tal como va en la etiqueta. */
    dosage: z.string().trim().max(200).optional(),
    /** Aviso legal de la ficha pública: no es medicamento, alérgenos, FDA… */
    legend: z.string().trim().max(2000).optional(),
    type: productTypeSchema.optional(),
    /**
     * Los productos que COMPONEN este, cuando su tipo se comporta como
     * paquete. Vacío o ausente en todo lo demás.
     *
     * Decisión de Anahí (26-ago): la composición se arma en el formulario
     * de PRODUCTO. `/admin/packages` sigue viva y sin tocar mientras se
     * planifica su retirada; por eso este campo y `package.items` conviven
     * hoy, con la misma forma exacta para que migrar sea copiar y no
     * traducir.
     *
     * El techo son 60 líneas. Un paquete de sesenta productos distintos ya
     * no es un paquete, es un catálogo, y el número existe para que un
     * error de tecleo no escriba un documento de miles de líneas.
     */
    items: z.array(catalogItemSchema).max(60).optional(),
    /** Los beneficios del catálogo a los que se acoge, en orden. */
    benefits: productBenefitsSchema.optional(),
    /** Variantes y costos adicionales: qué son y cuánto suman por moneda. */
    extraCosts: productExtraCostsSchema.optional(),
    /** Precio para socios. Mismo mapa que `prices`. Se ESCRIBE: ver `franchiseePrices`. */
    memberPrices: priceMapSchema.optional(),
    /**
     * Precio en promoción. Se DERIVA de `discount` (ver `promoPricesFrom`)
     * y lo escribe el servidor: dos fuentes para la misma rebaja se
     * contradicen en cuanto alguien toca una.
     */
    promoPrices: priceMapSchema.optional(),
    discount: productDiscountSchema.optional(),
    compensation: productCompensationSchema.optional(),
    validity: productValiditySchema.optional(),
    shipping: productShippingSchema.optional(),
    datasheet: productDatasheetSchema.optional(),
    facts: productFactsSchema.optional(),
    /** Mayorista: paquete de negocio digital. No sale en paquetes personales. */
    isWholesale: z.boolean().optional(),
    /** % extra para franquiciatarios activos. */
    franchiseeDiscountPercent: z.number().min(0).max(50).optional(),
    /** Publicado pero temporalmente sin vender. */
    isAvailable: z.boolean().optional(),
    volumePoints: volumePointsSchema.optional(),
    editorial: productEditorialSchema.optional(),
    status: productStatusSchema,
    sortOrder: z.number().int(),
});
/** Crear: aquí SÍ hay valores por omisión, que es donde tienen sentido. */
export const productInputSchema = productFieldsSchema.extend({
    slug: slugSchema,
    status: productStatusSchema.default("draft"),
    sortOrder: z.number().int().default(0),
});
/**
 * Editar: sin `slug` —cambiarlo rompería cada enlace ya compartido— y sin
 * valores por omisión, para que lo que no se manda no se toque.
 */
export const productUpdateSchema = productFieldsSchema.partial();
// ── Listado ──────────────────────────────────────────────
export const productListQuerySchema = z.object({
    search: z.string().trim().max(120).optional(),
    category: z.string().trim().max(60).optional(),
    status: productStatusSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(24),
    sort: z.enum(["recommended", "price-asc", "price-desc", "newest"]).default("recommended"),
});
export const productListSchema = z.object({
    items: z.array(productSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
    categories: z.array(z.string()),
});
export const productListResponseSchema = responseSchema(productListSchema);
export const productResponseSchema = responseSchema(z.object({ product: productSchema }));
export const productDeletedResponseSchema = responseSchema(z.object({ deleted: z.literal(true) }));
// ── Rutas ────────────────────────────────────────────────
export const PRODUCT_ROUTES = {
    /** Catálogo público: solo `published`. */
    publicList: "/api/products",
    publicBySlug: (slug) => `/api/products/${slug}`,
    /** Administración: todos los estados. Solo super-admin. */
    adminList: "/api/admin/products",
    adminCreate: "/api/admin/products",
    adminOne: (id) => `/api/admin/products/${id}`,
};
//# sourceMappingURL=product.js.map